// CRUD da lista de "Justificativas de Falta" — as opções do dropdown
// "Adicionar justificativa" na Chamada (ver AttendanceList.jsx) eram um array
// fixo no código; agora cada instituição tem a própria lista, editável aqui.
// O texto escolhido é copiado como string livre pra `presenca.observacao`
// (sem FK) — editar/apagar uma justificativa aqui nunca muda um registro de
// presença já lançado, só as opções oferecidas dali pra frente (ver
// backend/justificativas-falta.js).
import React, { useState, useEffect, useCallback } from 'react';
import { Pencil, Trash2, Check, X, Plus, Loader2 } from 'lucide-react';
import { useInstitution } from '../contexts/InstitutionContext';
import { usePermissao } from '../hooks/usePermissao';
import { useToast, useConfirm } from './Feedback';
import { Modal, Input, Button } from './ui';
import { API_URL } from '../api/config';
import { useApi } from '../api/useApi';

// `onChange` é chamado depois de qualquer criação/edição/remoção com
// sucesso — o pai (Chamada, em App.js) usa isso pra saber que precisa
// recarregar a lista que alimenta o dropdown de cada aluno.
function JustificativasFaltaModal({ isOpen, onClose, onChange }) {
  const { institutionId } = useInstitution();
  const token = localStorage.getItem('authToken');
  const { podeCriar, podeEditar, podeExcluir } = usePermissao('/');
  const toast = useToast();
  const confirm = useConfirm();

  const authHeaders = {
    'x-institution-id': institutionId,
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
  const api = useApi();

  const [lista, setLista] = useState([]);
  const [loading, setLoading] = useState(false);
  const [novoTexto, setNovoTexto] = useState('');
  const [salvandoNovo, setSalvandoNovo] = useState(false);
  const [editandoId, setEditandoId] = useState(null);
  const [editandoTexto, setEditandoTexto] = useState('');
  const [salvandoEdicao, setSalvandoEdicao] = useState(false);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/justificativas-falta`, { headers: authHeaders });
      if (!res.ok) throw new Error(`Erro HTTP: ${res.status}`);
      const body = await res.json();
      setLista(body?.data ?? (Array.isArray(body) ? body : []));
    } catch (err) {
      toast.error('Erro ao carregar justificativas.');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [institutionId, token]);

  useEffect(() => {
    if (isOpen) carregar();
  }, [isOpen, carregar]);

  const handleCriar = async (e) => {
    e.preventDefault();
    const texto = novoTexto.trim();
    if (!texto) return;
    setSalvandoNovo(true);
    try {
      const res = await api.post(`/api/justificativas-falta`, { texto });
      setNovoTexto('');
      toast.success(res?.message || 'Justificativa criada com sucesso.');
      await carregar();
      onChange?.();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSalvandoNovo(false);
    }
  };

  const iniciarEdicao = (item) => {
    setEditandoId(item.id);
    setEditandoTexto(item.texto);
  };

  const cancelarEdicao = () => {
    setEditandoId(null);
    setEditandoTexto('');
  };

  const salvarEdicao = async (id) => {
    const texto = editandoTexto.trim();
    if (!texto) return;
    setSalvandoEdicao(true);
    try {
      const res = await api.put(`/api/justificativas-falta/${id}`, { texto });
      cancelarEdicao();
      toast.success(res?.message || 'Justificativa atualizada com sucesso.');
      await carregar();
      onChange?.();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSalvandoEdicao(false);
    }
  };

  const excluir = async (item) => {
    if (
      !(await confirm(`Remover a justificativa "${item.texto}"?`, {
        title: 'Remover justificativa',
        confirmText: 'Remover',
        tone: 'danger',
      }))
    )
      return;
    try {
      const res = await api.del(`/api/justificativas-falta/${item.id}`);
      setLista((prev) => prev.filter((i) => i.id !== item.id));
      toast.success(res?.message || 'Justificativa removida com sucesso.');
      onChange?.();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Justificativas de Falta" maxWidth="md">
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
        Lista de motivos disponível no botão de justificativa da Chamada, própria desta instituição.
      </p>

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
        </div>
      ) : (
        <div className="space-y-1.5 max-h-[50vh] overflow-y-auto">
          {lista.length === 0 && (
            <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-4">
              Nenhuma justificativa cadastrada.
            </p>
          )}
          {lista.map((item) => (
            <div
              key={item.id}
              className="flex items-center gap-2 p-2 rounded-xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/60"
            >
              {editandoId === item.id ? (
                <>
                  <input
                    autoFocus
                    value={editandoTexto}
                    onChange={(e) => setEditandoTexto(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') salvarEdicao(item.id);
                      if (e.key === 'Escape') cancelarEdicao();
                    }}
                    className="flex-1 h-9 px-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  />
                  <button
                    type="button"
                    disabled={salvandoEdicao}
                    onClick={() => salvarEdicao(item.id)}
                    className="p-1.5 rounded-lg hover:bg-white dark:hover:bg-black/30 text-green-600 dark:text-green-400"
                  >
                    <Check className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    disabled={salvandoEdicao}
                    onClick={cancelarEdicao}
                    className="p-1.5 rounded-lg hover:bg-white dark:hover:bg-black/30 text-gray-500 dark:text-gray-400"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </>
              ) : (
                <>
                  <span className="flex-1 text-sm text-gray-800 dark:text-gray-200">
                    {item.texto}
                  </span>
                  {podeEditar && (
                    <button
                      type="button"
                      onClick={() => iniciarEdicao(item)}
                      className="p-1.5 rounded-lg hover:bg-white dark:hover:bg-black/30 text-gray-500 dark:text-gray-400"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                  )}
                  {podeExcluir && (
                    <button
                      type="button"
                      onClick={() => excluir(item)}
                      className="p-1.5 rounded-lg hover:bg-white dark:hover:bg-black/30 text-red-600 dark:text-red-400"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {podeCriar && (
        <form
          onSubmit={handleCriar}
          className="flex items-end gap-2 mt-4 pt-4 border-t border-gray-100 dark:border-gray-800"
        >
          <Input
            label="Nova justificativa"
            value={novoTexto}
            onChange={(e) => setNovoTexto(e.target.value)}
            placeholder="Ex: Saúde"
            className="flex-1"
          />
          <Button
            type="submit"
            variant="primary"
            icon={Plus}
            disabled={salvandoNovo || !novoTexto.trim()}
          >
            {salvandoNovo ? 'Adicionando...' : 'Adicionar'}
          </Button>
        </form>
      )}
    </Modal>
  );
}

export default JustificativasFaltaModal;
